import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Separator } from '@shared/components/ui/separator';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@shared/components/ui/form';
import { useAuthStore } from '@features/auth/hooks/useAuthStore';
import { clearCurrentPartner } from '@shared/utils/partner.utils';
import { DevRoleSelector } from './DevRoleSelector';
import { SocialAuthButtons } from './SocialAuthButtons';
import { loginSchema, type LoginFormValues } from './auth-schemas';

interface LoginFormProps {
  onShowRegister: () => void;
  onShowForgot: () => void;
}

export function LoginForm({ onShowRegister, onShowForgot }: LoginFormProps) {
  const navigate = useNavigate();
  const { login, socialLogin, error, clearError, pendingOtp } = useAuthStore();

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const { isSubmitting } = form.formState;

  const onSubmit = async (values: LoginFormValues) => {
    clearError();
    try {
      await login(values.email, values.password);
      // If the account requires a TOTP code, LoginPage will switch to the OTP
      // challenge view; only navigate once a full session exists.
      if (!useAuthStore.getState().pendingOtp && useAuthStore.getState().isAuthenticated) {
        clearCurrentPartner();
        toast.success('Signed in successfully');
        navigate('/', { replace: true });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to sign in');
    }
  };

  const handleSocial = async (provider: 'google' | 'passkey') => {
    clearError();
    try {
      await socialLogin(provider);
      if (useAuthStore.getState().isAuthenticated) {
        clearCurrentPartner();
        toast.success('Signed in successfully');
        navigate('/', { replace: true });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to sign in');
    }
  };

  const busy = isSubmitting || Boolean(pendingOtp);

  return (
    <>
      <div className="mb-8 text-center">
        <h2 className="mb-2 text-2xl font-semibold text-foreground">Sign in to your account</h2>
        <p className="text-sm text-muted-foreground">
          Enter your credentials to access your dashboard
        </p>
      </div>

      <DevRoleSelector />

      {error && (
        <Alert variant="destructive" icon={AlertCircle} className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="flex flex-col gap-5">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    className="h-11"
                    disabled={busy}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Password</FormLabel>
                  <button
                    type="button"
                    onClick={onShowForgot}
                    className="text-sm font-medium text-primary hover:text-primary/80"
                  >
                    Forgot password?
                  </button>
                </div>
                <FormControl>
                  <Input
                    type="password"
                    autoComplete="current-password"
                    className="h-11"
                    disabled={busy}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="h-11 w-full font-medium" disabled={busy}>
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </Button>
        </form>
      </Form>

      <div className="my-6 flex items-center gap-4">
        <Separator className="flex-1" />
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Or continue with
        </span>
        <Separator className="flex-1" />
      </div>

      <SocialAuthButtons
        onGoogle={() => handleSocial('google')}
        onPasskey={() => handleSocial('passkey')}
        disabled={busy}
      />

      <p className="mt-6 text-center text-sm text-muted-foreground">
        New to Vinr?{' '}
        <button
          type="button"
          onClick={onShowRegister}
          className="font-medium text-primary hover:text-primary/80"
        >
          Create an account
        </button>
      </p>
    </>
  );
}
